import type { ReactNode } from 'react';
import { Eyebrow } from '@bp-files/ui';

export interface SurfaceHeaderProps {
  /**
   * Surface name, e.g. "Users" — or, for a surface whose copy is a headline
   * rather than a name-plus-sentence pair (Files), the headline itself.
   */
  title: string;
  /**
   * A micro-label above the title, saying what kind of screen this is.
   *
   * **For a title that is a proper noun, not for one that is a description.**
   * The space root's `<h1>` is the room's own name (CON-22, FIL-2), which
   * announces *which* thing without saying what the reader is looking at —
   * whereas "Users" and "Folders shared with other agencies" already say it.
   * So this is one caller's, and adding a second means having that argument
   * about that screen.
   *
   * Not to be confused with the `SPEC · FIL-1 …` eyebrow below, which sat in
   * the same position and was scaffolding for a developer. Sentence case:
   * `<Eyebrow>` owns the uppercase and the tracking, and it renders a `<span>`,
   * so it adds nothing to the heading outline.
   */
  eyebrow?: string;
  /**
   * One line on what this surface does, in the product's voice. Optional: a
   * surface whose `title` is already a headline carries no separate summary
   * under it — two lines making the same point is noise, not reassurance.
   */
  summary?: string;
  /**
   * A page-level control, aligned to the title's trailing edge — Spaces'
   * Connect an agency, Files' recycle bin. Optional: most surfaces have no
   * single primary act to put here.
   *
   * **A bare control, not a wrapper.** Both callers pass one child, so neither
   * wraps it in a `role="group"`: a named group around a single button is a
   * container announced for no one, and `RecycleBinButton` renders as nothing
   * on an empty bin, so Files' would frequently have been empty as well as
   * named. `SpaceRoot` earns its group by having two acts.
   */
  actions?: ReactNode;
  /**
   * `hero` places the primary action after the complete header on mobile, then
   * returns it to the title's trailing edge at the desktop breakpoint.
   */
  actionPlacement?: 'trailing' | 'hero';
  children?: ReactNode;
}

/**
 * The heading block every surface opens with — title and one line.
 *
 * **Renamed from `SurfacePlaceholder` when Storage became real.** It started
 * life as a scaffold whose job was to name the requirement block a surface had
 * not yet implemented, and its own doc said to delete it once every surface
 * existed. What actually happened is that two of its three callers kept using it
 * as a header with real content underneath, which is the thing it turned out to
 * be for — so the component stayed and the name went.
 *
 * **The `spec` eyebrow went with the scaffolding it belonged to.** It rendered
 * `SPEC · FIL-1 … FIL-11` above the title, which pointed a developer at the
 * contract behind an unfinished surface and pointed everyone else at the fact
 * that this was somebody's project. The requirement ids are still stable and
 * still cited — in commits, PRs, and the comments in this file's neighbours,
 * where the audience is the person who needs them.
 *
 * **`actions` is a trigger only, never the control's state or its fetch.**
 * The Spaces surface's Create a space button sits here, upper-right of the
 * headline,
 * because a page-level act reads as one at the top of the page rather than as
 * the first row of a table beneath it. But `SpaceList` still owns
 * `CreateSpacePanel` and its `useSpaces().refresh` success handler — `App.tsx`
 * lifts only the `useState` toggle that opens it, passed to `SpaceList` as
 * `connectOpen`/`onCloseConnect`. Lifting the panel itself, or its fetch,
 * would either duplicate `useSpaces()` into a second source of truth or make
 * this component responsible for a refetch it has no business owning.
 *
 * That paragraph read "Files' Connect an agency" for two milestones; ADR-0032
 * moved the button and the table together, and the ownership split it describes
 * is exactly what did **not** change. ADR-0071 renamed the act — Connect made a
 * room and put an agency in it, and adding an agency now happens on a room that
 * already exists, from that room's roster — and the split held again.
 */
export function SurfaceHeader({
  title,
  eyebrow,
  summary,
  actions,
  actionPlacement = 'trailing',
  children,
}: SurfaceHeaderProps) {
  return (
    <section className="bp-animate-fade-in flex flex-col gap-5">
      {/*
        A plain div, not <header>: this is a heading block inside the main
        region, not a page-level banner landmark. A <header> here competes with
        the AppShell's real one.
      */}
      {actionPlacement === 'hero' ? (
        // The eyebrow sits *outside* the grid rather than in a spanning cell of
        // it. The action below pins itself to `lg:row-start-1`, so a first row
        // of grid content would put the primary control level with the
        // micro-label instead of with the title it belongs to.
        <div className="flex flex-col gap-1">
          <EyebrowLine eyebrow={eyebrow} />
          <div className="grid gap-x-4 gap-y-2 lg:grid-cols-[minmax(0,1fr)_auto]">
            <h1 className="text-h1 font-bold tracking-tight text-body">{title}</h1>
            {summary ? <p className="text-sm text-secondary">{summary}</p> : null}
            <div className="justify-self-start lg:col-start-2 lg:row-start-1 lg:self-center">
              {actions}
            </div>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          <EyebrowLine eyebrow={eyebrow} />
          <div className="flex items-center justify-between gap-4">
            <h1 className="text-h1 font-bold tracking-tight text-body">{title}</h1>
            {actions}
          </div>
          {/*
          No `max-w-prose`: capping a one-line summary at the 65ch reading
          width left it as a narrow column with the rest of the section's
          width sitting empty beside it — the same "old two-column" look the
          Storage meters and chart had before they were widened to match
          `Breakdown`'s table. A summary here is one sentence, not a
          paragraph a reading width protects; there is nothing for the cap to
          do except leave a gap.
        */}
          {summary ? <p className="text-sm text-secondary">{summary}</p> : null}
        </div>
      )}
      {children}
    </section>
  );
}

/** Absent for every surface but the space root — see `eyebrow` above. */
function EyebrowLine({ eyebrow }: { eyebrow: string | undefined }) {
  if (eyebrow === undefined) return null;

  return <Eyebrow>{eyebrow}</Eyebrow>;
}
